import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Star, Database, Sparkles, AlertCircle } from "lucide-react";
import useMeta from "@/hooks/useMeta";

export default function Ratings() {
    const { t } = useTranslation();
    useMeta({
        title: "Cómo valoramos los libros · RecomendaLibros",
        description: "Combinamos valoraciones reales de Hardcover (Goodreads-equivalent) y Google Books con curación literaria. Sin estrellas inventadas, sin algoritmos opacos.",
        canonical: "/valoraciones",
    });
    return (
        <main className="min-h-[calc(100vh-73px)] max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <Link to="/" className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors mb-6 inline-block">
                ← {t("nav.home", "Home")}
            </Link>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">{t("ratings.eyebrow")}</p>
            <h1
                className="font-display text-5xl md:text-6xl text-[var(--ink)] tracking-tight mb-4"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}
            >
                {t("ratings.title")}
            </h1>
            <p className="text-[var(--ink-muted)] text-lg mb-12">{t("ratings.lead")}</p>

            <section className="border-l-2 border-[var(--accent)] pl-6 mb-12">
                <h2 className="font-display text-2xl md:text-3xl text-[var(--ink)] mb-3 inline-flex items-center gap-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}>
                    <Database size={20} strokeWidth={1.5} className="text-[var(--accent)]" />
                    {t("ratings.sources_title")}
                </h2>
                <p className="text-[var(--ink-muted)] leading-relaxed mb-4">{t("ratings.sources_lead")}</p>
                <ul className="space-y-3 text-[var(--ink-muted)]">
                    <li className="flex gap-3">
                        <Star size={14} strokeWidth={1.5} className="text-[var(--accent)] flex-shrink-0 mt-1" />
                        <div>
                            <strong className="text-[var(--ink)]">Goodreads.</strong> {t("ratings.goodreads_desc")}
                        </div>
                    </li>
                    <li className="flex gap-3">
                        <Star size={14} strokeWidth={1.5} className="text-[var(--accent)] flex-shrink-0 mt-1" />
                        <div>
                            <strong className="text-[var(--ink)]">Amazon.</strong> {t("ratings.amazon_desc")}
                        </div>
                    </li>
                    <li className="flex gap-3">
                        <Star size={14} strokeWidth={1.5} className="text-[var(--accent)] flex-shrink-0 mt-1" />
                        <div>
                            <strong className="text-[var(--ink)]">OpenLibrary / crítica especializada.</strong> {t("ratings.openlibrary_desc")}
                        </div>
                    </li>
                </ul>
            </section>

            <section className="mb-12">
                <h2 className="font-display text-2xl md:text-3xl text-[var(--ink)] mb-3 inline-flex items-center gap-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}>
                    <Sparkles size={20} strokeWidth={1.5} className="text-[var(--accent)]" />
                    {t("ratings.how_title")}
                </h2>
                <p className="text-[var(--ink-muted)] leading-relaxed whitespace-pre-line">
                    {t("ratings.how_body")}
                </p>
            </section>

            <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-6">
                <h3 className="font-display text-xl text-[var(--ink)] mb-3 inline-flex items-center gap-2"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                    <AlertCircle size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                    {t("ratings.honest_title")}
                </h3>
                <p className="text-sm text-[var(--ink-muted)] leading-relaxed">{t("ratings.honest_body")}</p>
            </section>

            <p className="text-xs text-[var(--ink-dim)] italic mt-12 border-t border-[var(--border)] pt-8">
                {t("ratings.last_updated")}
            </p>
        </main>
    );
}
